import React, { useState } from 'react';
import { useField } from 'react-final-form';
import {
  Box, FormControl, FormHelperText, FormLabel, HStack, Input, Switch, Text,
} from '@chakra-ui/react';

interface Props {
  /** what this document is, for the helper copy ("purchase order" / "sales order" / "invoice") */
  docLabel: string;
  /** where the link travels next, e.g. "carries to the Sales Order and the invoice" */
  carriesTo?: string;
}

// item 1310 (Chad) — "[has tracking] → tracking URL": ONE optional shipment-tracking link per
// document. The switch is the affordance; the URL is the stored truth (switch off = clear it).
// Rendered identically on the PO, the Sales/Work Order and the Invoice forms, and the same
// pair sits on the PO→SO guided conversion (PurchaseOrderConvertModal) — never re-clone the
// copy, extend it here.
// item 1331 (Chad: "looks like shit… put that in a row below so we have more room for the URL
// and in line with the toggle") — ONE row, full width: the switch + label on the left, the URL
// input filling the rest of the row on the same baseline. Callers give it its own row.
const TrackingUrlField: React.FC<Props> = ({ docLabel, carriesTo }) => {
  const { input } = useField<string>('trackingUrl', { parse: (v) => v ?? '' });
  const [hasTracking, setHasTracking] = useState<boolean>(!!input.value);
  const on = hasTracking || !!input.value;
  return (
    <Box w="full">
      <FormControl>
        <HStack spacing={4} align="center" w="full">
          <HStack spacing={3} flexShrink={0} minW="140px">
            <Switch
              id="has-tracking"
              isChecked={on}
              onChange={(e) => {
                setHasTracking(e.target.checked);
                if (!e.target.checked) input.onChange('');
              }}
            />
            <FormLabel htmlFor="has-tracking" mb={0} fontSize="sm" cursor="pointer" whiteSpace="nowrap">Has tracking</FormLabel>
          </HStack>
          {on ? (
            <Input
              flex={1}
              type="url"
              placeholder="Carrier tracking URL"
              value={input.value ?? ''}
              onChange={(e) => input.onChange(e.target.value)}
              onBlur={input.onBlur}
              focusBorderColor="brandGreen.400"
            />
          ) : (
            <Text fontSize="xs" opacity={0.6}>{`No shipment tracking on this ${docLabel}.`}</Text>
          )}
        </HStack>
        {on && (
          <FormHelperText>
            {`Prints on the ${docLabel} PDF as a live link${carriesTo ? ` and ${carriesTo}` : ''}; the invoice email shows it as "Track your shipment".`}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

TrackingUrlField.defaultProps = { carriesTo: undefined };

export default TrackingUrlField;
